import type {ParameterDifferences} from "#graph/diffing/types"
import {getGraphNodesByInstanceIds} from "#graph/helpers/collect"
import {graphParametersDifferences} from "#graph/helpers/diffing"
import {traverseGraphParameters} from "#graph/helpers/parameters"
import {BaseGraphNode} from "#graph/nodes/base/base-graph-node"
import type {NodeParameters} from "#graph/nodes/base/types"

export class GraphNodeDifferences<
    Node extends {
        parameters: NodeParameters
        instanceId: string
        [Symbol.iterator]: () => Iterator<Node>
        parents: Set<Node>
        replaceParameters: {
            (newParameters: NodeParameters, disableValidation: true): void
            (newParameters: NodeParameters, disableValidation?: false): void
        }
    },
> {
    constructor(
        protected root: Node,
        public addedNodes: Set<Node>,
        public modifiedNodes: Map<Node, ParameterDifferences>,
        public deletedNodes: Set<Node>,
    ) {}

    private apply(value: "oldValue" | "newValue") {
        const expectedValue = value === "oldValue" ? "newValue" : "oldValue"

        const valuesEqual = (a: unknown, b: unknown) => {
            const differences = graphParametersDifferences({value: a}, {value: b})
            return differences.length === 0
        }

        const nodeUpdates = new Map<Node, NodeParameters>()
        for (const [node, nodeDifferences] of this.modifiedNodes.entries()) {
            if (nodeDifferences.length === 0) continue

            const newParameters = traverseGraphParameters(node.parameters, (value) => value instanceof BaseGraphNode)
            for (const {path, [value]: newValue, [expectedValue]: previousValue} of nodeDifferences) {
                if (path.length === 0) throw new Error("Path length is 0")

                const pathParts = path.split(".")
                let current = newParameters as NodeParameters | Array<unknown>
                for (let i = 0; i < pathParts.length - 1; i++) {
                    const part = pathParts[i]
                    const steppedDown = Array.isArray(current) ? current[parseInt(part)] : current[part]
                    if (Array.isArray(steppedDown) || (typeof steppedDown === "object" && steppedDown !== null))
                        current = steppedDown as NodeParameters | Array<unknown>
                    else throw new Error(`Could not find path ${path} in parameters`)
                }

                const key = pathParts[pathParts.length - 1]
                if (Array.isArray(current)) {
                    if (key === "length") {
                        if (!valuesEqual(current.length, previousValue))
                            throw new Error(`Expected array length ${previousValue} but found ${current.length} in path ${path}`)
                        if (typeof newValue !== "number") throw new Error(`Expected number value but found ${typeof newValue} in path ${path}`)
                        current.length = newValue
                    } else {
                        const arrayIndex = parseInt(key)
                        if ((arrayIndex >= current.length && newValue !== undefined) || arrayIndex < 0)
                            throw new Error(`Array index ${arrayIndex} out of bounds in path ${path}, current length is ${current.length}`)
                        if (arrayIndex < current.length) {
                            if (!valuesEqual(current[arrayIndex], previousValue))
                                throw new Error(`Expected array value ${previousValue} but found ${current[arrayIndex]} in path ${path}`)
                            current[arrayIndex] = newValue
                        }
                    }
                } else if (typeof current === "object" && current !== null) {
                    if (!valuesEqual(current[key], previousValue))
                        throw new Error(`Expected object value ${previousValue} but found ${current[key]} in path ${path}`)
                    current[key] = newValue
                } else throw new Error("Current is not an object")
            }

            nodeUpdates.set(node, newParameters)
        }

        for (const [node, newParameters] of nodeUpdates.entries()) node.replaceParameters(newParameters)

        const removedNodes = value === "oldValue" ? this.addedNodes : this.deletedNodes
        if (removedNodes.size > 0) {
            getGraphNodesByInstanceIds(this.root).forEach((node) => {
                removedNodes.forEach((removedNode) => node.parents.delete(removedNode))
            })
        }

        const reintroducedNodes = value === "oldValue" ? this.deletedNodes : this.addedNodes
        reintroducedNodes.forEach((node) => node.replaceParameters(node.parameters, true))
    }

    applyForward = () => this.apply("newValue")
    applyBackward = () => this.apply("oldValue")
}
